/**
 * Web twin of native `CustomMapMarker` + `TaskMapMarker`: a white-framed
 * thumbnail with a pointer tongue and a soft ground shadow under the tip.
 * The bottom of the root is the task's spot.
 */
import { imageOutline } from 'ionicons/icons';
import IonIcon from '../../components/IonIcon';

/** Native `TaskMapMarker` STATUS_COLORS: success, warning, primary. */
const STATUS_COLORS: Record<string, string> = {
  completed: 'rgba(39, 174, 96, 1)',
  inProgress: 'rgba(243, 156, 18, 1)',
  pending: 'rgba(79, 215, 223, 1)',
};

export function markerColor(status: string): string {
  return STATUS_COLORS[status] ?? STATUS_COLORS.pending;
}

const SIZES = {
  default: { thumb: 52, pad: 6, icon: 28, tongue: 10, radius: 10, shadowW: 30, shadowH: 8 },
  large: { thumb: 70, pad: 6, icon: 38, tongue: 13, radius: 15, shadowW: 36, shadowH: 9 },
} as const;

interface Props {
  photoUrl: string | null;
  status: string;
  title: string;
  size?: keyof typeof SIZES;
}

/** Visual only: the map's marker owns the click and the keyboard focus. */
export default function TaskMapMarker({ photoUrl, status, title, size = 'default' }: Props) {
  const s = SIZES[size];
  return (
    <div
      role="img"
      aria-label={title}
      data-testid="task-map-marker"
      className="flex flex-col items-center transition active:scale-95"
    >
      <span
        className="block bg-white shadow-[0_2px_10px_rgba(42,60,82,0.18)]"
        style={{ padding: s.pad, borderRadius: s.radius }}
      >
        {photoUrl ? (
          <img
            src={photoUrl}
            alt=""
            draggable={false}
            className="block object-cover"
            style={{ width: s.thumb, height: s.thumb }}
          />
        ) : (
          <span
            className="flex items-center justify-center text-white"
            style={{ width: s.thumb, height: s.thumb, backgroundColor: markerColor(status) }}
          >
            <IonIcon icon={imageOutline} size={s.icon} />
          </span>
        )}
      </span>
      <span
        aria-hidden="true"
        className="block size-0 border-solid border-x-transparent border-t-white"
        style={{ borderLeftWidth: s.tongue, borderRightWidth: s.tongue, borderTopWidth: s.tongue }}
      />
      <span
        aria-hidden="true"
        className="mt-px block rounded-[50%]"
        style={{
          width: s.shadowW,
          height: s.shadowH,
          background:
            'radial-gradient(closest-side, rgba(42,60,82,0.22), rgba(42,60,82,0.11) 60%, rgba(42,60,82,0) 100%)',
        }}
      />
    </div>
  );
}
